// Sugestão inicial do quadro de vans: regiões cabem juntas até a lotação de
// referência; uma região maior que a lotação vira várias vans, mantendo os
// bairros contíguos. A operadora ajusta a partir daqui.
const byBairro = (a, b) => String(a.bairro || '').localeCompare(String(b.bairro || ''), 'pt-BR')
  || String(a.nome || '').localeCompare(String(b.nome || ''), 'pt-BR');

const chunkRegion = (region, capacity) => {
  const count = Math.ceil(region.pessoas.length / capacity);
  const people = [...region.pessoas].sort(byBairro);
  const size = Math.ceil(people.length / count);
  return Array.from({ length: count }, (_, index) => ({
    ...region,
    id: `${region.id}-${index + 1}`,
    pessoas: people.slice(index * size, (index + 1) * size),
  })).filter((item) => item.pessoas.length);
};

// Lotação de referência: a capacidade mais comum da frota (no empate, a menor).
// Um micro-ônibus isolado não deve fazer a sugestão juntar regiões demais.
export function typicalCapacity(capacities = []) {
  const counts = new Map();
  for (const value of capacities.map(Number).filter((item) => item > 0)) counts.set(value, (counts.get(value) || 0) + 1);
  if (!counts.size) return null;
  return [...counts].sort((a, b) => b[1] - a[1] || a[0] - b[0])[0][0];
}

export function packRegionsByCapacity(regions = [], capacity) {
  const cap = Number(capacity);
  if (!Number.isFinite(cap) || cap < 1) return regions.map((region) => ({ ...region, qtdVeiculos: 1 }));
  const items = regions.flatMap((region) => (region.pessoas.length > cap ? chunkRegion(region, cap) : [region]))
    .sort((a, b) => b.pessoas.length - a.pessoas.length || String(a.regiao).localeCompare(String(b.regiao), 'pt-BR'));
  const bins = [];
  for (const item of items) {
    const bin = bins.find((candidate) => candidate.count + item.pessoas.length <= cap);
    if (bin) { bin.items.push(item); bin.count += item.pessoas.length; } else bins.push({ items: [item], count: item.pessoas.length });
  }
  return bins.map(({ items: [first, ...rest] }) => (rest.length ? {
    id: `agrupado-${[first, ...rest].map((item) => item.id).join('-')}`,
    regiao: [...new Set([first, ...rest].map((item) => item.regiao))].join(' + '),
    pessoas: [first, ...rest].flatMap((item) => item.pessoas),
    qtdVeiculos: 1,
    origens: [...new Set([first, ...rest].flatMap((item) => item.origens || [item.regiao]))],
    subgrupos: null,
  } : { ...first, qtdVeiculos: 1 }));
}

// Agrupa os passageiros de uma van por região, na ordem em que aparecem; dentro
// da região, vizinhos de bairro ficam juntos.
export function peopleByRegion(people = []) {
  const groups = [];
  for (const person of people) {
    const name = person.regiao || (person.cidade ? `${person.cidade} - Sem região` : 'Região não informada');
    let group = groups.find((item) => item.name === name);
    if (!group) { group = { name, people: [] }; groups.push(group); }
    group.people.push(person);
  }
  return groups.map((group) => ({ ...group, people: [...group.people].sort(byBairro) }));
}

// Pedidos com até 30 minutos de diferença saem juntos na mesma viagem. A janela
// conta a partir do primeiro horário, para não encadear 02:00, 02:25, 02:50...
// A API aceita até 1 hora; horários mais distantes continuam pela seleção manual.
export const TRIP_WINDOW_MINUTES = 30;
const minutesOf = (horario) => {
  const match = String(horario || '').match(/^(\d{1,2}):(\d{2})/);
  return match ? Number(match[1]) * 60 + Number(match[2]) : null;
};

// Pedidos da lista "A fazer" que formam a mesma viagem: mesma transportadora,
// destino, data e sentido, com horários dentro da janela.
export function groupOrdersIntoTrips(items = [], windowMinutes = TRIP_WINDOW_MINUTES) {
  const routes = new Map();
  for (const item of items) {
    const key = [item.cliente_id, item.empresa_destino_id, String(item.data || '').slice(0, 10), item.trecho].join('|');
    if (!routes.has(key)) routes.set(key, []);
    routes.get(key).push(item);
  }
  const trips = [];
  for (const [key, orders] of routes) {
    const sorted = [...orders].sort((a, b) => (minutesOf(a.horario) ?? 0) - (minutesOf(b.horario) ?? 0));
    let current = null;
    for (const item of sorted) {
      const minutes = minutesOf(item.horario) ?? 0;
      if (!current || minutes - current.start > windowMinutes) {
        current = { key: `${key}|${String(item.horario || '').slice(0, 5)}`, start: minutes, items: [] };
        trips.push(current);
      }
      current.items.push(item);
    }
  }
  return trips.map(({ key, items }) => ({
    key,
    items,
    first: items[0],
    last: items[items.length - 1],
    passengers: items.reduce((sum, item) => sum + (Number(item.total_passageiros) || 0), 0),
  })).sort((a, b) => String(a.first.horario).localeCompare(String(b.first.horario)));
}
